'use client';

import { useCallback, useSyncExternalStore } from 'react';
import type { MoversUniverse } from '@/lib/api-client';

/** Shared state for the Movers universe filter (issue #244). Drives both
 *  the /markets/[type] page filter pill and the dashboard Top Gainers /
 *  Losers / Most Active tiles so a single selection controls every
 *  Movers surface.
 *
 *  Persisted in localStorage under `siq.moversUniverse`. Cross-tab sync
 *  via the `storage` event so changing the filter in one tab updates
 *  others within ~1 tick. Default is 'r1000' per the original plan —
 *  broader than the historical SP500-only behavior, narrow enough to
 *  exclude penny-stock noise.
 *
 *  Built on `useSyncExternalStore` so SSR always returns DEFAULT_UNIVERSE
 *  and the client only diverges AFTER hydration completes. The earlier
 *  `useState(loadInitial)` shape read localStorage during the first
 *  client render — when localStorage held a non-default value, React 18
 *  detected the className mismatch on the chip ("This won't be patched
 *  up") and left the SSR'd chip class frozen, even though the SWR
 *  fetcher (which reads from state) correctly used the localStorage
 *  value. Result: chip said "R1000" while the table loaded the "All"
 *  universe. useSyncExternalStore avoids this entirely. */

const STORAGE_KEY = 'siq.moversUniverse';
const DEFAULT_UNIVERSE: MoversUniverse = 'r1000';
const VALID = new Set<MoversUniverse>(['all', 'sp500', 'r1000']);

function getSnapshot(): MoversUniverse {
  try {
    const v = window.localStorage.getItem(STORAGE_KEY);
    if (v && VALID.has(v as MoversUniverse)) return v as MoversUniverse;
  } catch { /* ignore */ }
  return DEFAULT_UNIVERSE;
}

function getServerSnapshot(): MoversUniverse {
  return DEFAULT_UNIVERSE;
}

// Subscriber list — `useSyncExternalStore` calls `subscribe` once per
// mounted consumer. Wake every consumer on either a same-tab write
// (setUniverse below) or a cross-tab `storage` event. Without this,
// setUniverse would update localStorage but other in-page consumers
// (dashboard tiles, ticker tape) wouldn't know to re-read.
const subscribers = new Set<() => void>();

function subscribe(cb: () => void): () => void {
  subscribers.add(cb);
  const onStorage = (e: StorageEvent) => {
    if (e.key === STORAGE_KEY) cb();
  };
  window.addEventListener('storage', onStorage);
  return () => {
    subscribers.delete(cb);
    window.removeEventListener('storage', onStorage);
  };
}

function notifyLocal(): void {
  subscribers.forEach((cb) => cb());
}

export function useMoversUniverse(): [MoversUniverse, (v: MoversUniverse) => void] {
  const universe = useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);

  const setUniverse = useCallback((v: MoversUniverse) => {
    try { window.localStorage.setItem(STORAGE_KEY, v); } catch { /* ignore */ }
    notifyLocal();
  }, []);

  return [universe, setUniverse];
}
